import { FC, ReactNode } from 'react'
import AppBar from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import Toolbar from '@mui/material/Toolbar'
import { shrinkableColumnSx } from './Table/Table'

export interface PageWrapperProps {
    /** Content rendered before the title in the toolbar (e.g. sidebar toggle button) */
    toolbarLeft?: ReactNode
    /** Page title or breadcrumb content rendered in the toolbar */
    title?: ReactNode
    /** Action buttons rendered on the right side of the toolbar */
    actions?: ReactNode
    /** Content rendered above the toolbar (e.g. license warning banners) */
    banner?: ReactNode
    /** Page content */
    children: ReactNode
    /** When true, removes the maxWidth constraint */
    fullWidth?: boolean
    /**
     * Fits the page to the height of its scrolling parent instead of growing with the content, so a
     * child that scrolls on its own (a `stickyHeader` table) keeps the toolbar and page controls in view.
     */
    fillHeight?: boolean
}

/**
 * Common page wrapper providing consistent layout structure:
 * - Banner across the full width, above a section with maxWidth: 1920px (unless fullWidth)
 * - Sticky AppBar toolbar with title and actions
 * - Content area with px: 3, py: 2 padding
 * - With fillHeight, a page-height flex column whose content area may shrink
 */
const PageWrapper: FC<PageWrapperProps> = ({
    toolbarLeft,
    title,
    actions,
    banner,
    children,
    fullWidth,
    fillHeight,
}) => {
    const sectionStyles = {
        position: 'relative' as const,
        width: '100%',
        ...(fullWidth ? {} : { maxWidth: '1920px' }),
        ...(fillHeight ? { flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column' as const } : {}),
    }

    return (
        <div style={fillHeight ? { height: '100%', display: 'flex', flexDirection: 'column' } : undefined}>
            {banner}
            <section style={sectionStyles}>
                <AppBar position="sticky" data-test-id="app-bar" color="transparent">
                    <Toolbar
                        disableGutters
                        sx={{
                            backgroundColor: (theme) => theme.palette.background.default,
                            borderBottomWidth: '1px',
                            borderBottomColor: (theme) => theme.palette.divider,
                            borderBottomStyle: 'solid',
                            px: 3,
                        }}
                    >
                        {toolbarLeft}
                        {title}
                        <Box sx={{ flexGrow: 1 }} />
                        {actions}
                    </Toolbar>
                </AppBar>
                <Box
                    sx={[
                        { px: 3, py: 2 },
                        fillHeight
                            ? {
                                  ...shrinkableColumnSx,
                                  flex: '1 1 auto',
                                  // Only the table's chain absorbs the height limit. Toolbars set an explicit
                                  // min-height, which would otherwise let a wrapped second row be squeezed out.
                                  '& > *': { flexShrink: 0 },
                              }
                            : {},
                    ]}
                >
                    {children}
                </Box>
            </section>
        </div>
    )
}

export default PageWrapper
